import { useId } from "react";

import { DiagramFrame } from "@/app/features/diagrams/components/shared/DiagramFrame";
import { DiagramMarkers } from "@/app/features/diagrams/components/shared/DiagramMarkers";
import { DiagramNode } from "@/app/features/diagrams/components/shared/DiagramNode";
import { S1000DDeliveryTargets } from "@/app/features/diagrams/components/blog/s1000d/S1000DDeliveryTargets";
import {
  type DiagramTopologySpec,
  validateDiagramTopology,
} from "@/app/features/diagrams/functions/diagramTypes";

// The point of the diagram is the seam, not the stage count. Everything above
// the publication model knows S1000D; everything below it knows the
// application. Drawn as one trunk narrowing into the model and fanning out of
// it, so the model reads as the place the pipeline pivots rather than as
// another box in a row.
const pipelineTopologySpec: DiagramTopologySpec = {
  nodes: [
    "source",
    "parse-validate",
    "resolve",
    "publication-model",
    "web-publication",
    "offline-package",
    "downstream",
  ],
  junctions: [{ id: "fan-out", kind: "split", x: 350, y: 396 }],
  connectors: [
    {
      id: "source-to-parse",
      source: { nodeId: "source", anchor: "bottom-center" },
      target: { nodeId: "parse-validate", anchor: "top-center" },
      meaning: "Data modules and assets enter the pipeline",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "parse-to-resolve",
      source: { nodeId: "parse-validate", anchor: "bottom-center" },
      target: { nodeId: "resolve", anchor: "top-center" },
      meaning: "Validated modules pass to reference resolution",
      semanticType: "control",
      lineStyle: "solid",
    },
    {
      id: "resolve-to-model",
      source: { nodeId: "resolve", anchor: "bottom-center" },
      target: { nodeId: "publication-model", anchor: "top-center" },
      meaning: "Resolved content becomes the publication model",
      semanticType: "merged",
      lineStyle: "solid",
    },
    {
      id: "model-to-fanout",
      source: { nodeId: "publication-model", anchor: "bottom-center" },
      target: { junctionId: "fan-out" },
      meaning: "One model feeds every delivery target",
      semanticType: "merged",
      lineStyle: "solid",
    },
    {
      id: "fanout-to-web",
      source: { junctionId: "fan-out" },
      target: { nodeId: "web-publication", anchor: "top-center" },
      meaning: "Browser delivery target",
      semanticType: "success",
      lineStyle: "solid",
    },
    {
      id: "fanout-to-offline",
      source: { junctionId: "fan-out" },
      target: { nodeId: "offline-package", anchor: "top-center" },
      meaning: "Packaged offline delivery target",
      semanticType: "success",
      lineStyle: "solid",
    },
    {
      id: "fanout-to-downstream",
      source: { junctionId: "fan-out" },
      target: { nodeId: "downstream", anchor: "top-center" },
      meaning: "Structured content consumed by another system",
      semanticType: "success",
      lineStyle: "dashed",
    },
  ],
};

if (process.env.NODE_ENV !== "production") {
  validateDiagramTopology(pipelineTopologySpec);
}

export function S1000DConversionPipelineVisual() {
  const titleId = useId();
  const descId = useId();

  return (
    <DiagramFrame
      width={660}
      height={510}
      title="S1000D Conversion Pipeline Architecture"
      description="Pipeline diagram. S1000D source data modules and assets are parsed and validated against the project schema and BREX, then references, repository content, publication structure and applicability are resolved into an application-oriented publication model. That single model feeds a browser publication, a packaged offline application, and downstream systems, so a new delivery target does not require changes to the parser."
      titleId={titleId}
      descId={descId}
      viewBox="0 0 700 530"
      frame={{
        kind: "boundary",
        label: "S1000D CONVERSION BOUNDARY",
        x: 20,
        y: 20,
        width: 660,
        height: 490,
      }}
    >
      <DiagramMarkers prefix="s1k-" />

      {/* SOURCE */}
      <DiagramNode
        x={210}
        y={50}
        width={280}
        height={48}
        label="S1000D Source"
        sublabel="CSDB or Exported Packages"
        variant="capsule"
      />

      <path
        d="M 350 98 L 350 128"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.45"
        markerEnd="url(#s1k-arrow-neutral)"
      />

      {/* PARSE + VALIDATE */}
      <DiagramNode
        x={190}
        y={130}
        width={320}
        height={52}
        label="Parse & Validate"
        sublabel="Issue Detection · Schema · BREX"
        variant="standard"
      />

      <path
        d="M 350 182 L 350 212"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.45"
        markerEnd="url(#s1k-arrow-neutral)"
      />

      {/* RESOLVE */}
      <DiagramNode
        x={170}
        y={214}
        width={360}
        height={52}
        label="Resolve"
        sublabel="References · Repository · Publication Structure"
        variant="standard"
      />

      <path
        d="M 350 266 L 350 300"
        fill="none"
        stroke="var(--ga-brass, #9c6b3e)"
        strokeWidth="1.8"
        markerEnd="url(#s1k-arrow-neutral)"
      />

      {/* THE SEAM — dashed rule across the diagram */}
      <path
        d="M 60 288 L 250 288"
        fill="none"
        stroke="var(--ga-brass, #9c6b3e)"
        strokeWidth="1"
        strokeDasharray="4 4"
        opacity="0.5"
      />
      <path
        d="M 450 288 L 640 288"
        fill="none"
        stroke="var(--ga-brass, #9c6b3e)"
        strokeWidth="1"
        strokeDasharray="4 4"
        opacity="0.5"
      />
      <text
        x="60"
        y="280"
        fill="var(--ga-brass, #9c6b3e)"
        className="text-[10px] font-mono font-bold"
      >
        knows S1000D
      </text>
      <text
        x="640"
        y="280"
        textAnchor="end"
        fill="var(--ga-brass, #9c6b3e)"
        className="text-[10px] font-mono font-bold"
      >
        knows the application
      </text>

      {/* PUBLICATION MODEL — the seam */}
      <DiagramNode
        x={150}
        y={302}
        width={400}
        height={64}
        eyebrow="THE SEAM"
        label="Publication Model"
        sublabel="Decomposed DMC · Reference Graph · Applicability as Data"
        variant="brass"
      />

      <S1000DDeliveryTargets />
    </DiagramFrame>
  );
}
